import React, { ReactNode, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Folder,
  Folders,
  GraduationCap,
  Loader,
  Pause,
  Search,
  SquareCheck,
  Trash2,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";
import DataPickerCst from "@components/InputCst/DataPickerCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import ListItemCardCst from "@components/ListItemCardCst";
import { ListItemBadge } from "@components/ListItemCardCst/types";
import PaginatorCst from "@components/PaginatorCst";
import SortMenuCst from "@components/SortMenuCst";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { formatDate } from "@utils/utilsDate";
import StaticOptions from "src/assets/static/options.json";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const PAGE_SIZE = 10;
const ALL_VALUE = "ALL";

const ENROLLMENT_SORT_KEYS = ["id", "date-desc", "date-asc"] as const;
const LO_SORT_KEYS = ["id-asc", "id-desc", "title-asc", "title-desc"] as const;

type EnrollmentSort = (typeof ENROLLMENT_SORT_KEYS)[number];
type LoSort = (typeof LO_SORT_KEYS)[number];

interface MassiveEnrollment {
  id: number | string;
  operatorId?: number | string;
  enrollType?: string;
  enrollDate?: string;
  status?: string;
}

interface MassiveEnrollmentLo {
  id: number | string;
  title?: string;
  type?: string;
}

interface GroupEnrollmentStatusModalProps {
  open: boolean;
  onClose: () => void;
  corporateId: string;
  group?: { id: number | string; name?: string } | null;
}

const STATUS_BADGE: Record<
  string,
  { variant: ListItemBadge["variant"]; icon: ReactNode }
> = {
  COMPLETED: { variant: "success", icon: <SquareCheck /> },
  TODO: { variant: "info", icon: <Pause /> },
  IN_PROGRESS: { variant: "default", icon: <Loader /> },
  ERROR: { variant: "error", icon: <TriangleAlert /> },
  DELETED: { variant: "default", icon: <Trash2 /> },
};

const LO_TYPE_ICON = (type?: string): ReactNode => {
  switch (type) {
    case "COURSE":
      return <Folder size={16} />;
    case "PATH":
      return <Folders size={16} />;
    default:
      return <GraduationCap size={16} />;
  }
};

const staticOptionValues = (key: string): string[] =>
  (StaticOptions.find((option) => option.key === key)?.options as string[]) ||
  [];

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toTime = (value?: string): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortEnrollments = (
  rows: MassiveEnrollment[],
  sort: EnrollmentSort,
): MassiveEnrollment[] => {
  const copy = [...rows];
  switch (sort) {
    case "date-desc":
      return copy.sort((a, b) => toTime(b.enrollDate) - toTime(a.enrollDate));
    case "date-asc":
      return copy.sort((a, b) => toTime(a.enrollDate) - toTime(b.enrollDate));
    default:
      return copy.sort((a, b) => toNumber(a.id) - toNumber(b.id));
  }
};

const sortLos = (
  rows: MassiveEnrollmentLo[],
  sort: LoSort,
): MassiveEnrollmentLo[] => {
  const copy = [...rows];
  const byTitle = (a: MassiveEnrollmentLo, b: MassiveEnrollmentLo) =>
    String(a.title || "").localeCompare(String(b.title || ""), undefined, {
      sensitivity: "base",
    });
  switch (sort) {
    case "id-desc":
      return copy.sort((a, b) => toNumber(b.id) - toNumber(a.id));
    case "title-asc":
      return copy.sort(byTitle);
    case "title-desc":
      return copy.sort((a, b) => byTitle(b, a));
    default:
      return copy.sort((a, b) => toNumber(a.id) - toNumber(b.id));
  }
};

const toUtcMidnightIso = (value: any): string | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
  ).toISOString();
};

const filterBoxSx = {
  flex: 1,
  minWidth: 170,
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  "& .MuiFormHelperText-root": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const compactFieldSx = { minWidth: "0", width: "100%" };

interface HeaderProps {
  eyebrow: ReactNode;
  title: string;
  onClose: () => void;
}

const ModalHeader: React.FC<HeaderProps> = ({ eyebrow, title, onClose }) => {
  const theme = useTheme();
  return (
    <DialogTitle sx={{ padding: "20px 24px 8px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: theme.palette.text.primary,
            }}
          >
            {eyebrow}
          </Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
              mt: 0.5,
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
        >
          <X size={20} />
        </IconButton>
      </Box>
    </DialogTitle>
  );
};

interface ListStateProps {
  isLoading: boolean;
  error: any;
  isEmpty: boolean;
  children: ReactNode;
}

const ListState: React.FC<ListStateProps> = ({
  isLoading,
  error,
  isEmpty,
  children,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-groups-v2");
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || isEmpty) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 5,
          color: theme.palette.text.primary,
        }}
      >
        {error
          ? t("enrollment-status-modal.error")
          : t("enrollment-status-modal.empty")}
      </Box>
    );
  }
  return <>{children}</>;
};

interface EnrollmentsViewProps {
  corporateId: string;
  group: { id: number | string; name?: string };
  onClose: () => void;
  onOpenLos: (enrollment: MassiveEnrollment) => void;
}

const EnrollmentsView: React.FC<EnrollmentsViewProps> = ({
  corporateId,
  group,
  onClose,
  onOpenLos,
}) => {
  const { t } = useTranslation("users-groups-v2");
  const { t: tInput } = useTranslation("input");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [operatorId, setOperatorId] = useState("");
  const [enrollDate, setEnrollDate] = useState<any>(null);
  const [status, setStatus] = useState<string>(ALL_VALUE);
  const [enrollType, setEnrollType] = useState<string>(ALL_VALUE);
  const [sort, setSort] = useState<EnrollmentSort>("id");

  const debouncedOperatorId = useDebouncedValue(operatorId, 400);

  const listUrl = `${ServicesURL.getEnrollmentMassive}?corporateId=${corporateId}&groupId=${group.id}`;

  const fieldFilters = useMemo(
    () => ({
      operatorId: debouncedOperatorId || undefined,
      enrollDate: toUtcMidnightIso(enrollDate),
      status: status === ALL_VALUE ? undefined : status,
      enrollType: enrollType === ALL_VALUE ? undefined : enrollType,
    }),
    [debouncedOperatorId, enrollDate, status, enrollType],
  );

  const { items, totalItems, totalPages, isLoading, error } =
    usePaginatedList<MassiveEnrollment>(listUrl, {
      page,
      pageSize,
      fieldFilters,
    });

  const sortedItems = useMemo(
    () => sortEnrollments(items, sort),
    [items, sort],
  );

  const statusOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: String(t("enrollment-status-modal.all")) },
      ...staticOptionValues("multiple_enrollment_status").map((value) => ({
        value,
        label: String(
          t(`enrollment-status-modal.status.${value.toLowerCase()}`),
        ),
      })),
    ],
    [t],
  );

  const enrollTypeOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: String(t("enrollment-status-modal.all")) },
      ...staticOptionValues("enroll_type").map((value) => ({
        value,
        label: String(tInput(`options.enroll_type.${value.toLowerCase()}`)),
      })),
    ],
    [t, tInput],
  );

  const statusLabel = (value?: string): string =>
    value
      ? String(
          t(`enrollment-status-modal.status.${value.toLowerCase()}`, value),
        )
      : "-";

  const enrollTypeLabel = (value?: string): string =>
    value
      ? String(tInput(`options.enroll_type.${value.toLowerCase()}`, value))
      : "-";

  return (
    <>
      <ModalHeader
        eyebrow={
          <>
            <Users size={16} />
            <Typography sx={{ fontSize: "13px" }}>
              {group.name || group.id} (ID: {group.id})
            </Typography>
          </>
        }
        title={String(t("enrollment-status-modal.title"))}
        onClose={onClose}
      />

      <DialogContent sx={{ padding: "0 24px 8px" }}>
        <Box
          sx={{ pt: "16px", display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={filterBoxSx}>
              <InputCst
                id="group-enrollment-status-operator"
                label={t("enrollment-status-modal.operator-id") || ""}
                minWidth={false}
                value={operatorId}
                onChange={(e: any) => {
                  setOperatorId(e.target.value);
                  setPage(1);
                }}
              />
            </Box>
            <Box sx={{ ...filterBoxSx, minWidth: 210 }}>
              <DataPickerCst
                id="group-enrollment-status-date"
                label={t("enrollment-status-modal.request-date") || ""}
                value={enrollDate}
                onChange={(value: any) => {
                  setEnrollDate(value);
                  setPage(1);
                }}
                isFilterActive={!!enrollDate}
                sx={compactFieldSx}
              />
            </Box>
            <Box sx={{ ...filterBoxSx, flex: 1.3 }}>
              <SelectCst
                id="group-enrollment-status-status"
                label={t("enrollment-status-modal.status-label") || ""}
                value={status}
                options={statusOptions}
                onChange={(value: any) => {
                  setStatus(value || ALL_VALUE);
                  setPage(1);
                }}
                isFilterActive={status !== ALL_VALUE}
                sx={compactFieldSx}
              />
            </Box>
            <Box sx={{ ...filterBoxSx, flex: 1.5 }}>
              <SelectCst
                id="group-enrollment-status-type"
                label={t("enrollment-status-modal.enroll-type") || ""}
                value={enrollType}
                options={enrollTypeOptions}
                onChange={(value: any) => {
                  setEnrollType(value || ALL_VALUE);
                  setPage(1);
                }}
                isFilterActive={enrollType !== ALL_VALUE}
                sx={compactFieldSx}
              />
            </Box>
            <Box sx={{ marginLeft: "auto" }}>
              <SortMenuCst
                id="group-enrollment-status-sort"
                value={sort}
                options={ENROLLMENT_SORT_KEYS.map((key) => ({
                  value: key,
                  label: String(t(`enrollment-status-modal.sort.${key}`)),
                }))}
                onChange={(next) => setSort(next as EnrollmentSort)}
              />
            </Box>
          </Box>

          <ListState
            isLoading={isLoading}
            error={error}
            isEmpty={sortedItems.length === 0}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {sortedItems.map((row) => {
                const badge = row.status ? STATUS_BADGE[row.status] : undefined;
                return (
                  <ListItemCardCst
                    key={row.id}
                    title={`${t("enrollment-status-modal.operator-id")}: ${
                      row.operatorId ?? "-"
                    }`}
                    subtitle={{
                      segments: [
                        {
                          label: `${t(
                            "enrollment-status-modal.enroll-type",
                          )}: ${enrollTypeLabel(row.enrollType)}`,
                        },
                      ],
                    }}
                    details={{
                      row1: `${t("enrollment-status-modal.request-date")}: ${
                        row.enrollDate ? formatDate(row.enrollDate) : "-"
                      }`,
                    }}
                    badges={[
                      {
                        label: statusLabel(row.status),
                        variant: badge?.variant ?? "default",
                        icon: badge?.icon,
                      },
                    ]}
                    actions={[
                      {
                        label: String(
                          t("enrollment-status-modal.learning-objects"),
                        ),
                        variant: "outlined",
                        onClick: () => onOpenLos(row),
                      },
                    ]}
                  />
                );
              })}
            </Box>
          </ListState>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "4px 24px 20px" }} />
    </>
  );
};

interface LosViewProps {
  corporateId: string;
  enrollment: MassiveEnrollment;
  onClose: () => void;
  onBack: () => void;
}

const LosView: React.FC<LosViewProps> = ({
  corporateId,
  enrollment,
  onClose,
  onBack,
}) => {
  const { t } = useTranslation("users-groups-v2");
  const { t: tInput } = useTranslation("input");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [loId, setLoId] = useState("");
  const [loType, setLoType] = useState<string>(ALL_VALUE);
  const [sort, setSort] = useState<LoSort>("id-asc");

  const debouncedSearch = useDebouncedValue(search, 400);
  const debouncedLoId = useDebouncedValue(loId, 400);

  const listUrl = `${ServicesURL.getEnrollmentGroupLo.replace(
    ":idCorporate",
    corporateId,
  )}?enrollmentId=${enrollment.id}`;

  const fieldFilters = useMemo(
    () => ({
      title: debouncedSearch || undefined,
      id: debouncedLoId || undefined,
      type: loType === ALL_VALUE ? undefined : loType,
    }),
    [debouncedSearch, debouncedLoId, loType],
  );

  const { items, totalItems, totalPages, isLoading, error } =
    usePaginatedList<MassiveEnrollmentLo>(listUrl, {
      page,
      pageSize,
      fieldFilters,
    });

  const sortedItems = useMemo(() => sortLos(items, sort), [items, sort]);

  const loTypeOptions = useMemo(
    () => [
      { value: ALL_VALUE, label: String(t("enrollment-status-modal.all")) },
      ...staticOptionValues("enrollment_status_lo_type").map((value) => ({
        value,
        label: String(
          tInput(`options.enrollment_status_lo_type.${value.toLowerCase()}`),
        ),
      })),
    ],
    [t, tInput],
  );

  const loTypeLabel = (value?: string): string =>
    value
      ? String(
          tInput(
            `options.enrollment_status_lo_type.${value.toLowerCase()}`,
            value,
          ),
        )
      : "-";

  return (
    <>
      <ModalHeader
        eyebrow={
          <Typography sx={{ fontSize: "13px" }}>
            {t("enrollment-status-modal.operator-id")}:{" "}
            {enrollment.operatorId ?? "-"} |{" "}
            {t("enrollment-status-modal.request-date")}:{" "}
            {enrollment.enrollDate ? formatDate(enrollment.enrollDate) : "-"}
          </Typography>
        }
        title={String(t("enrollment-status-modal.lo-title"))}
        onClose={onClose}
      />

      <DialogContent sx={{ padding: "0 24px 8px" }}>
        <Box
          sx={{ pt: "16px", display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ ...filterBoxSx, flex: 1, minWidth: 180 }}>
              <InputCst
                id="group-enrollment-lo-search"
                placeholder={t("enrollment-status-modal.search") || ""}
                minWidth={false}
                inputSearch
                value={search}
                onChange={(e: any) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                startAdornment={<Search size={16} style={{ marginRight: 8 }} />}
              />
            </Box>
            <Box sx={{ ...filterBoxSx, width: 160 }}>
              <InputCst
                id="group-enrollment-lo-id"
                label={t("enrollment-status-modal.lo-id") || ""}
                minWidth={false}
                value={loId}
                onChange={(e: any) => {
                  setLoId(e.target.value);
                  setPage(1);
                }}
              />
            </Box>
            <Box sx={filterBoxSx}>
              <SelectCst
                id="group-enrollment-lo-type"
                label={t("enrollment-status-modal.lo-type") || ""}
                value={loType}
                options={loTypeOptions}
                onChange={(value: any) => {
                  setLoType(value || ALL_VALUE);
                  setPage(1);
                }}
                isFilterActive={loType !== ALL_VALUE}
                sx={compactFieldSx}
              />
            </Box>
            <Box sx={{ marginLeft: "auto" }}>
              <SortMenuCst
                id="group-enrollment-lo-sort"
                value={sort}
                options={LO_SORT_KEYS.map((key) => ({
                  value: key,
                  label: String(t(`enrollment-status-modal.sort.${key}`)),
                }))}
                onChange={(next) => setSort(next as LoSort)}
              />
            </Box>
          </Box>

          <ListState
            isLoading={isLoading}
            error={error}
            isEmpty={sortedItems.length === 0}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {sortedItems.map((lo) => (
                <ListItemCardCst
                  key={lo.id}
                  icon={LO_TYPE_ICON(lo.type)}
                  title={lo.title || String(lo.id)}
                  subtitle={{ id: lo.id, activityType: loTypeLabel(lo.type) }}
                  actions={[]}
                />
              ))}
            </Box>
          </ListState>

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <ButtonSecondary size="md" onClick={onBack}>
          {t("enrollment-status-modal.back")}
        </ButtonSecondary>
        <Box sx={{ flex: 1 }} />
      </DialogActions>
    </>
  );
};

interface ContentProps {
  corporateId: string;
  group: { id: number | string; name?: string };
  onClose: () => void;
}

const GroupEnrollmentStatusContent: React.FC<ContentProps> = ({
  corporateId,
  group,
  onClose,
}) => {
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<MassiveEnrollment | null>(null);

  if (selectedEnrollment) {
    return (
      <LosView
        key={selectedEnrollment.id}
        corporateId={corporateId}
        enrollment={selectedEnrollment}
        onClose={onClose}
        onBack={() => setSelectedEnrollment(null)}
      />
    );
  }

  return (
    <EnrollmentsView
      corporateId={corporateId}
      group={group}
      onClose={onClose}
      onOpenLos={setSelectedEnrollment}
    />
  );
};

const GroupEnrollmentStatusModal: React.FC<GroupEnrollmentStatusModalProps> = ({
  open,
  onClose,
  corporateId,
  group,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {open && group && (
        <GroupEnrollmentStatusContent
          corporateId={corporateId}
          group={group}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
};

export default GroupEnrollmentStatusModal;
